/* eslint-disable max-lines */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form } from 'reactstrap';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import { UserWithEmployee } from '../UserSelectionModal';
import PerformanceFeedbackService from '../../../../../services/PerformanceFeedback';
import PriorDiscussionsTable from '../PriorDiscussionsTable';
import FeedbackSummary from './FeedbackSummary';
import { useFeedbackForm } from './Form/useFeedbackForm';
import { INITIAL_FORM_DATA } from './Form/formConstants';
import {
  UserInfoSection,
  PolicySection,
  FeedbackDescriptionSection,
  RadioSection,
  ExpectationSection,
} from './Form/FormSections';

interface FeedbackFormProps {
  isOpen: boolean;
  toggle: () => void;
  selectedUsers?: UserWithEmployee[];
  onSuccess?: () => void;
  editMode?: boolean;
  feedbackId?: string;
}

interface FormData {
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfFeedback: string;
  immediateFeedback: string;
  expectationForImprovement: string;
  emailOfFeedback: string;
}

const FeedbackForm = ({
  isOpen,
  toggle,
  selectedUsers = [],
  onSuccess,
  editMode = false,
  feedbackId,
}: FeedbackFormProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);
  const schoolId = currentSchool?.value;
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [submittedForms, setSubmittedForms] = useState<Map<string, FormData>>(new Map());
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const currentUser = selectedUsers[currentUserIndex];
  const isLastUser = currentUserIndex === selectedUsers.length - 1;
  const hasMultipleUsers = !editMode && selectedUsers.length > 1;

  const { formData, errors, policyViolations, priorDiscussions, handleInputChange, validateForm, setFormData } =
    useFeedbackForm(isOpen, token, currentUser, submittedForms, editMode, feedbackId);

  useEffect(() => {
    if (!isOpen) {
      setCurrentUserIndex(0);
      setSubmittedForms(new Map());
      setShowSummary(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
  }, [isOpen, currentUserIndex]);

  const saveCurrentForm = () => {
    if (currentUser) {
      const newSubmittedForms = new Map(submittedForms);
      newSubmittedForms.set(currentUser.id, { ...formData });
      setSubmittedForms(newSubmittedForms);
    }
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev + 1);
  };

  const handlePrevious = () => {
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent, approved: boolean = true) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(approved);
    } else {
      saveCurrentForm();
      setShowSummary(true);
    }
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(false);
    } else {
      await submitDirectly(false);
    }
  };

  const submitDirectly = async (approved: boolean) => {
    if (!validateForm()) return;
    saveCurrentForm();

    setIsSubmitting(true);
    try {
      const service = PerformanceFeedbackService(token);
      const finalForms = new Map(submittedForms);
      if (currentUser) {
        finalForms.set(currentUser.id, { ...formData });
      }

      const entries = Array.from(finalForms.entries());
      const feedbacks = entries.map(([userId, formData]: [string, FormData]) => ({
        userId,
        name: formData.name,
        dateOfIncident: formData.dateOfIncident,
        position: formData.position,
        department: formData.department,
        supervisor: formData.supervisor,
        dateOfHire: formData.dateOfHire,
        policyViolationIds: formData.policyViolationIds,
        descriptionOfFeedback: formData.descriptionOfFeedback,
        immediateFeedback: formData.immediateFeedback,
        expectationForImprovement: formData.expectationForImprovement,
        emailOfFeedback: formData.emailOfFeedback,
        approved,
      }));

      await service.create({ schoolId, feedbacks });

      toast.success(
        approved
          ? `${feedbacks.length === 1 ? 'Feedback submitted' : `${feedbacks.length} feedbacks submitted`} successfully!`
          : `${feedbacks.length === 1 ? 'Draft saved' : `${feedbacks.length} drafts saved`} successfully!`,
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error submitting feedbacks:', error);
      toast.error(error?.response?.data?.message || 'Error submitting feedbacks. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEditForm = async (approved: boolean = true) => {
    if (!feedbackId) return;

    setIsSubmitting(true);
    try {
      const service = PerformanceFeedbackService(token);
      const updateData = {
        schoolId,
        dateOfIncident: formData.dateOfIncident,
        policyViolationIds: formData.policyViolationIds,
        descriptionOfFeedback: formData.descriptionOfFeedback,
        immediateFeedback: formData.immediateFeedback,
        expectationForImprovement: formData.expectationForImprovement,
        emailOfFeedback: formData.emailOfFeedback,
        approved,
      };

      await service.update(feedbackId, updateData);

      toast.success(approved ? 'Feedback updated successfully!' : 'Draft saved successfully!');

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error updating feedback:', error);
      toast.error(error?.response?.data?.message || 'Error updating feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitAllForms = async (approved: boolean = true) => {
    setIsSubmitting(true);
    try {
      const service = PerformanceFeedbackService(token);
      const entries = Array.from(submittedForms.entries());
      const feedbacks = entries.map(([userId, formData]: [string, FormData]) => ({
        userId,
        name: formData.name,
        dateOfIncident: formData.dateOfIncident,
        position: formData.position,
        department: formData.department,
        supervisor: formData.supervisor,
        dateOfHire: formData.dateOfHire,
        policyViolationIds: formData.policyViolationIds,
        descriptionOfFeedback: formData.descriptionOfFeedback,
        immediateFeedback: formData.immediateFeedback,
        expectationForImprovement: formData.expectationForImprovement,
        emailOfFeedback: formData.emailOfFeedback,
        approved,
      }));

      await service.create({ schoolId, feedbacks });

      toast.success(
        approved
          ? `${feedbacks.length === 1 ? 'Feedback submitted' : `${feedbacks.length} feedbacks submitted`} successfully!`
          : `${feedbacks.length === 1 ? 'Draft saved' : `${feedbacks.length} drafts saved`} successfully!`,
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error submitting feedbacks:', error);
      toast.error(error?.response?.data?.message || 'Error submitting feedbacks. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEdit = () => setShowSummary(false);

  const handleCancel = () => {
    setCurrentUserIndex(0);
    setSubmittedForms(new Map());
    setShowSummary(false);
    setFormData(INITIAL_FORM_DATA);
    toggle();
  };

  if (showSummary && !editMode) {
    return (
      <FeedbackSummary
        isOpen={isOpen}
        toggle={handleCancel}
        submittedForms={submittedForms}
        policyViolations={policyViolations}
        onBackToEdit={handleBackToEdit}
        onSubmit={(approved) => submitAllForms(approved)}
        isSubmitting={isSubmitting}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="xl" style={{ maxWidth: '1200px' }}>
      <ModalHeader toggle={handleCancel}>
        {editMode ? 'Edit Feedback' : 'Feedback Form'}
        {hasMultipleUsers && (
          <span className="ml-2 text-muted" style={{ fontSize: '0.9rem' }}>
            ({currentUserIndex + 1} of {selectedUsers.length})
          </span>
        )}
      </ModalHeader>
      <ModalBody>
        <div ref={modalBodyRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form onSubmit={handleSubmit}>
            <UserInfoSection formData={formData} errors={errors} onChange={handleInputChange} />

            <PriorDiscussionsTable discussions={priorDiscussions} />

            <PolicySection
              formData={formData}
              errors={errors}
              policyViolations={policyViolations}
              onChange={handleInputChange}
            />

            <FeedbackDescriptionSection
              value={formData.descriptionOfFeedback}
              error={errors.descriptionOfFeedback}
              onChange={(value) => handleInputChange('descriptionOfFeedback', value)}
            />

            <RadioSection
              title="Immediate Feedback"
              label="Did you speak with the employee about what you witness and how they can improve?"
              name="immediateFeedback"
              value={formData.immediateFeedback}
              onChange={(value) => handleInputChange('immediateFeedback', value)}
            />

            <ExpectationSection
              value={formData.expectationForImprovement}
              error={errors.expectationForImprovement}
              onChange={(value) => handleInputChange('expectationForImprovement', value)}
            />

            <RadioSection
              title="Email of Feedback"
              label="Do you want the staff member to receive the copy of the feedback?"
              name="emailOfFeedback"
              value={formData.emailOfFeedback}
              onChange={(value) => handleInputChange('emailOfFeedback', value)}
            />
          </Form>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <div className="ml-auto">
          {!editMode && hasMultipleUsers && currentUserIndex > 0 && (
            <Button color="outline-secondary" onClick={handlePrevious} className="mr-2">
              <i className="fas fa-arrow-left mr-2" />
              Previous
            </Button>
          )}
          {!editMode && hasMultipleUsers && !isLastUser ? (
            <Button color="primary" onClick={handleNext}>
              <i className="fas fa-arrow-right mr-2" />
              Next
            </Button>
          ) : (
            <>
              <Button color="warning" onClick={handleSaveDraft} disabled={isSubmitting} className="mr-2">
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2" />
                    Save Draft
                  </>
                )}
              </Button>
              <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2" />
                    {hasMultipleUsers ? 'Review & Submit' : 'Submit'}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default FeedbackForm;
